import {
  ErrorLabel,
  FieldLabel,
  Link,
  RequiredIndicator,
  useTestTag,
  combineRefs,
  EmptyFieldLabel,
  Dialog,
} from '@myosh/odin-components';
import type { DynamicFieldComponentProps } from '@myosh/odin-components';
import clsx from 'clsx';
import React, { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRiskRatingMatrixConfigQuery } from '../../../redux/services/risk-matrix';
import {
  additionalRiskCellItems,
  additionalRiskLevelCaptions,
  findRiskMatrixCaption,
  findSelectedRiskRating,
  formatRiskRatingDisplayValue,
  riskMatrixCellsSorted,
  uniqueRiskMatrixColumns,
} from './risk-rating-functions';
import RiskRatingMatrix from './risk-rating-matrix.component';
import reducer, { RiskRatingBaseActionType } from './risk-rating-reducer';
import type { RiskRatingBaseState } from './risk-rating-reducer';
import { cloneDeep } from 'lodash';
import type { RiskRatingValueDisplayKey } from '../../../@types/forms';
import type { RiskMatrixData, RiskMatrixDataItem, RiskMatrixRatingValue } from '../../../@types/risk-matrix-field';

export interface RiskRatingProps extends DynamicFieldComponentProps<RiskMatrixRatingValue> {
  displayDescription?: boolean;
  valueDisplayConfig?: Array<RiskRatingValueDisplayKey>;
  riskMatrixId: number;
}

function RiskRating({
  label,
  value,
  readOnly,
  required,
  error,
  onChange,
  displayDescription,
  valueDisplayConfig,
  riskMatrixId,
}: RiskRatingProps) {
  const initialState: RiskRatingBaseState = {
    riskMatrixCells: [],
    displayValue: '',
    currentDescription: '',
    selectedDescription: '',
  };

  const [state, dispatcher] = useReducer(reducer, initialState);
  const [riskMatrixVisible, setRiskMatrixVisible] = useState<boolean>(false);
  const riskMatrixPanel = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();
  const tagCreator = useTestTag('risk-rating');

  const { data: riskMatrixData, isFetching } = useRiskRatingMatrixConfigQuery(riskMatrixId);

  useEffect(() => {
    // The first item is a header row
    const cellPlaceholder = new Map<string, RiskMatrixData>();
    //1:1 cell should be empty
    cellPlaceholder.set('header', [{}]);

    if (riskMatrixData && riskMatrixData.length > 0 && !isFetching) {
      const matrixDataByLikelihood = cloneDeep(riskMatrixData).sort(
        (riskMatrix1, riskMatrix2) => (riskMatrix1.likelihood.position ?? 0) - (riskMatrix2.likelihood.position ?? 0)
      );
      for (const riskMatrixElement of matrixDataByLikelihood) {
        const likelihoodRowKey =
          (riskMatrixElement?.likelihood?.caption?.translations[0].value as string) +
          String(riskMatrixElement?.likelihood?.id);
        const consequenceColumns = uniqueRiskMatrixColumns(cellPlaceholder.get('header') ?? [], riskMatrixElement);
        const riskCellItems = additionalRiskCellItems(riskMatrixElement);
        const riskLevelCaptions = additionalRiskLevelCaptions(riskMatrixElement);

        const riskLevelProperties = {
          ratingId: riskCellItems?.ratingId,
          rating: riskCellItems?.rating,
          category: riskCellItems?.category,
          consequencePosition: riskMatrixElement.consequence.position,
          riskLevelCaptions: {
            ...riskMatrixElement.riskLevel.riskLevelCaptions,
            ...riskLevelCaptions,
          },
        };

        if (!consequenceColumns) {
          cellPlaceholder.set('header', [...(cellPlaceholder.get('header') ?? []), riskMatrixElement.consequence]);
        }

        if (!cellPlaceholder.has(likelihoodRowKey)) {
          cellPlaceholder.set(likelihoodRowKey, [
            riskMatrixElement.likelihood,
            {
              ...riskMatrixElement.riskLevel,
              ...riskLevelProperties,
            },
          ]);
        } else {
          cellPlaceholder.set(likelihoodRowKey, [
            ...(cellPlaceholder.get(likelihoodRowKey) ?? []),
            {
              ...riskMatrixElement.riskLevel,
              ...riskLevelProperties,
            },
          ]);
        }

        if (riskLevelProperties.ratingId === value?.id) {
          const riskRatingDescriptionValue = findRiskMatrixCaption(riskMatrixElement.riskLevel.descriptionCaption);
          dispatcher({
            type: RiskRatingBaseActionType.UpdateCurrentDescription,
            currentDescription: riskRatingDescriptionValue,
          });
        }
      }

      const transformedRiskMatrixCells = riskMatrixCellsSorted(cellPlaceholder);

      dispatcher({
        type: RiskRatingBaseActionType.SetRiskMatrixCells,
        riskMatrixCells: transformedRiskMatrixCells,
      });
    }
  }, [riskMatrixData, isFetching, value]);

  const onRiskMatrixOpen = () => {
    if (!readOnly) {
      setRiskMatrixVisible(true);
    }
  };

  const onRiskMatrixClose = () => {
    setRiskMatrixVisible(false);
  };

  const onRatingCellClick = (riskMatrixCell: RiskMatrixDataItem) => {
    const newDisplayValue = formatRiskRatingDisplayValue(riskMatrixCell, valueDisplayConfig);
    const newRatingDescription = findRiskMatrixCaption(riskMatrixCell.descriptionCaption);

    dispatcher({
      type: RiskRatingBaseActionType.UpdateRiskRatingProperties,
      riskRatingId: riskMatrixCell.ratingId,
      displayValue: newDisplayValue,
      selectedDescription: newRatingDescription,
    });

    onChange?.({
      id: riskMatrixCell.ratingId,
      rating: riskMatrixCell.rating,
      category: riskMatrixCell.category,
      riskLevel: findRiskMatrixCaption(riskMatrixCell.caption),
      consequence: riskMatrixCell.riskLevelCaptions?.consequenceCaption,
      likelihood: riskMatrixCell.riskLevelCaptions?.likelihoodCaption,
      displayValue: newDisplayValue,
    });

    onRiskMatrixClose();
  };

  const displayValue = useMemo(
    () =>
      state.displayValue ||
      (value &&
        formatRiskRatingDisplayValue(
          findSelectedRiskRating(state.riskMatrixCells, Number(state.riskRatingId || value.id)),
          valueDisplayConfig
        )),
    [value?.id, state.riskRatingId, state.riskMatrixCells.length]
  );
  const description = state.selectedDescription || state.currentDescription;

  const buttonClasses = clsx(
    'text-left',
    {
      'decoration-gray-2 pointer-events-none': readOnly,
    },
    {
      'border border-error': !value && error && !readOnly,
    }
  );

  const divClasses = clsx('flex flex-col gap-1 text-sm leading-4 [&>button]:ml-0', {
    'py-1 mx-1': value && !readOnly,
  });

  const combinedRef = combineRefs(riskMatrixPanel, tagCreator('select'));
  return (
    <>
      {label && (
        <div className="flex">
          <FieldLabel label={label} />
          <RequiredIndicator readOnly={readOnly} required={required} />
        </div>
      )}
      <div ref={combinedRef} className={divClasses}>
        {value ? (
          <>
            <Link classNames={buttonClasses} onClick={onRiskMatrixOpen}>
              {displayValue}
            </Link>
            {displayDescription && (
              <>
                <p className="font-bold">{`${t('description')}: `}</p>
                <p ref={tagCreator('description')}>{description}</p>
              </>
            )}
          </>
        ) : readOnly ? (
          <EmptyFieldLabel />
        ) : (
          <>
            <Link classNames={buttonClasses} onClick={onRiskMatrixOpen}>
              {t('please-select')}
            </Link>
            {error && <ErrorLabel>{error}</ErrorLabel>}
          </>
        )}
      </div>
      {riskMatrixVisible && (
        <Dialog
          header={t('risk-matrix')}
          visible={riskMatrixVisible}
          onClose={onRiskMatrixClose}
          contentClassName="custom-visible-scroll !pt-0 !pb-4"
        >
          <RiskRatingMatrix riskMatrixData={state.riskMatrixCells} onRatingCellClick={onRatingCellClick} />
        </Dialog>
      )}
    </>
  );
}

export default RiskRating;
